import { parseNumberInput } from '@/utils/approval/expensePreApproval';
import { localize } from '@/utils/approval/localize';

/**
 * ENT. 프로젝트(비용) 품의(213) — 사용 예산 + 지급 정보 + 분류별 집계.
 * 저장 attributes 는 평탄형(budgetItemValue …)이고 서버가 APV_SUB.CL02 JSON 으로 다시 묶음
 */

export const ENT_EXPENSE_TYPE = 213;

export const isEntExpenseType = (apvType: number): boolean =>
  apvType === ENT_EXPENSE_TYPE;

/** 사용 예산 목록은 엔터 법인 고정 */
export const ENT_SAP_COMPANY_CODE = '5000';

export const ENT_BUDGET_SEASON = 'YY';

/** G/L(Kstar)별 세부 항목 — SAP 응답에 없어 프론트에서 관리 */
export const ENT_KSTAR_ITEMS: Record<string, string[]> = {
  '43130113': ['타이틀곡', '수록곡'],
  '43130115': ['마스터 등'],
  '43130117': ['타이틀곡', '수록곡'],
  '43130119': ['의상'],
  '43130135': ['자켓촬영', '뮤비촬영', '쇼케이스', '비주얼디렉터'],
  '43130121': ['자켓촬영', '뮤비촬영', '쇼케이스'],
  '43130123': ['타이틀곡', '수록곡', '기타영상'],
  '43130125': ['세트', '포토', '디자인'],
  '43130139': ['기타개발비'],
  '43130181': ['인스타', '틱톡', '유튜브', '스포티파이', '기타바이럴'],
  '43130183': ['커뮤니티마케팅'],
  '43130185': ['인스타', '틱톡', '유튜브', '스포티파이', '기타바이럴'],
  '43130173': ['방송프로모션'],
  '43130187': ['제작', 'MC출연료', '진행비용'],
  '43130189': ['팬클럽관리', '미디어관리', '기타마케팅비'],
  '43130159': ['외주매니지먼트'],
  '43130191': ['음원편집'],
  '43130193': ['의상'],
  '43130195': ['음악방송', '국내활동', '국내행사', '해외행사'],
  '43130197': ['헤어관리', '음악방송', '국내활동', '국내행사', '해외행사'],
  '43130199': ['무대제작'],
  '43130201': ['기타활동비'],
  '43130151': ['복리후생비'],
  '43130155': ['여비교통비'],
  '43130149': ['차량유지비'],
  '43130163': ['숙소관리비'],
  '43130161': ['보컬', '랩', '안무', '언어', '체력', '기타'],
  '43130147': ['음원작업', '기타용역'],
  '43130167': ['감가상각비'],
  '43130175': ['임직원급여'],
  '43130177': ['커뮤니티마케팅', '바이럴마케팅', '내부마케팅', '기타마케팅'],
  '43130143': ['일반', '시상식'],
  '43130145': ['국내활동', '국내행사', '해외행사', '국내공연', '해외공연'],
  '43130141': [
    '헤어관리',
    '국내활동',
    '국내행사',
    '해외행사',
    '국내공연',
    '해외공연',
  ],
  '43130179': ['피부관리', '성형시술', '기타관리'],
  '43130165': ['기타진행비'],
  '43130205': ['지급수수료'],
  '43130207': ['제작진행비'],
  '43130209': ['자체콘텐츠'],
  '43130203': ['방송프로그램'],
  '43130221': ['외주용역'],
  '43130223': ['장비대여', '장소대여'],
  '43130225': ['번역비용', '기타비용'],
  '43130227': ['제작진행비'],
};

export const getKstarItems = (code: string): string[] =>
  ENT_KSTAR_ITEMS[code] ?? [];

// ===== SAP 응답 =====

export type EntCostCenterOption = { value: string; label: string };

export const toCostCenterOptions = (data: unknown): EntCostCenterOption[] =>
  (Array.isArray(data) ? data : []).flatMap((row) => {
    const r = row as { KOSTL?: unknown; LTEXT?: unknown } | null;
    if (!r || typeof r.KOSTL !== 'string') return [];
    const label = (typeof r.LTEXT === 'string' ? r.LTEXT : '')
      .replace(/\([^)]*\)/g, '')
      .trim();
    return [{ value: r.KOSTL, label }];
  });

export type EntBudgetCategory = {
  /** SAP G/L(Kstar) */
  code: string;
  label: string;
  budgetTotl: number;
  remain: number;
};

export type EntBudget = {
  categories: EntBudgetCategory[];
  total: number;
  remaining: number;
  usageRate: number;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseNumberInput(value);
  return NaN;
};

const numberOrZero = (value: unknown): number => {
  const n = toNumber(value);
  return Number.isFinite(n) ? n : 0;
};

export const formatEntNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  const [intPart, decimal] = String(value).split('.');
  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${
    decimal ? `.${decimal}` : ''
  }`;
};

export const calcUsageRate = (total: number, remaining: number): number =>
  total > 0 ? Math.round(((total - remaining) / total) * 100) : 0;

export const toEntBudget = (data: unknown): EntBudget | null => {
  const rows = Array.isArray(data) ? data : [];
  const categories = rows.flatMap((row) => {
    const r = row as {
      Kstar?: unknown;
      KstarText?: unknown;
      BudgetTotl?: unknown;
      Remain?: unknown;
    } | null;
    if (!r || typeof r.Kstar !== 'string') return [];
    return [
      {
        code: r.Kstar,
        label: (typeof r.KstarText === 'string' && r.KstarText) || r.Kstar,
        budgetTotl: numberOrZero(r.BudgetTotl),
        remain: numberOrZero(r.Remain),
      },
    ];
  });
  if (categories.length === 0) return null;
  const total = categories.reduce((sum, c) => sum + c.budgetTotl, 0);
  const remaining = categories.reduce((sum, c) => sum + c.remain, 0);
  return {
    categories,
    total,
    remaining,
    usageRate: calcUsageRate(total, remaining),
  };
};

export const getBudgetYearOptions = (now = new Date()): string[] => {
  const y = now.getFullYear();
  return [y - 1, y, y + 1].map(String);
};

export const getDefaultBudgetYear = (now = new Date()): string =>
  String(now.getFullYear());

// ===== 폼 상태 =====

export type EntExpenseRow = {
  id: string;
  category: string;
  /** 예산 응답 도착 전(편집 복원)에도 저장할 수 있게 보관 */
  categoryLabel: string;
  item: string;
  cost: string;
  remark: string;
};

export type EntExpenseState = {
  budgetItem: string;
  budgetItemLabel: string;
  budgetYear: string;
  rows: EntExpenseRow[];
  budget: EntBudget | null;
};

let idSeq = 0;
const nextRowId = () => {
  idSeq += 1;
  return `ent-row-${Date.now().toString(36)}-${idSeq}`;
};

export const createEntExpenseRow = (
  partial: Partial<Omit<EntExpenseRow, 'id'>> = {},
): EntExpenseRow => ({
  id: nextRowId(),
  category: '',
  categoryLabel: '',
  item: '',
  cost: '',
  remark: '',
  ...partial,
});

export const createEntExpenseState = (): EntExpenseState => ({
  budgetItem: '',
  budgetItemLabel: '',
  budgetYear: getDefaultBudgetYear(),
  rows: [createEntExpenseRow()],
  budget: null,
});

/** 예산 응답에 없는 분류는 비움 */
export const syncRowsToBudget = (
  rows: EntExpenseRow[],
  budget: EntBudget | null,
): EntExpenseRow[] =>
  rows.map((r) => {
    if (!r.category) return r;
    const found = budget?.categories.find((c) => c.code === r.category);
    if (!found) return { ...r, category: '', categoryLabel: '', item: '' };
    return found.label === r.categoryLabel
      ? r
      : { ...r, categoryLabel: found.label };
  });

/** 이탈 경고 비교 키 — 행 id·예산 조회 결과는 제외 */
export const entExpenseKey = (state: EntExpenseState | null): string =>
  state
    ? JSON.stringify([
        state.budgetItem,
        state.budgetYear,
        state.rows.map(({ id: _id, categoryLabel: _l, ...row }) => row),
      ])
    : '';

// ===== 금번 품의 사용 예정 =====

export type EntTotalAmountRow = {
  label: string;
  /** 서버 계약 키(오탈자 포함) */
  budgetTotl: number;
  remain: number;
  amount: number;
};

export const calcAccumulatedRate = (row: EntTotalAmountRow): number =>
  row.budgetTotl > 0
    ? Math.round(
        ((row.budgetTotl - row.remain + row.amount) / row.budgetTotl) * 100,
      )
    : 0;

/** 순서는 객체 키 순서 — 숫자형 G/L 오름차순, 미분류('-')는 뒤 */
export const computeEntTotalAmount = (
  rows: EntExpenseRow[],
  budget: EntBudget | null,
): EntTotalAmountRow[] => {
  const byCategory: Record<string, { label: string; amount: number }> = {};
  let unclassifiedIdx = 0;
  rows.forEach((r) => {
    const raw = parseNumberInput(r.cost);
    const amount = Number.isFinite(raw) ? Math.round(raw) : 0;
    if (!r.category && amount === 0) return;
    if (r.category) {
      const prev = byCategory[r.category];
      if (prev) prev.amount += amount;
      else byCategory[r.category] = { label: r.categoryLabel, amount };
      return;
    }
    byCategory[`__unclassified_${unclassifiedIdx}`] = { label: '-', amount };
    unclassifiedIdx += 1;
  });
  return Object.entries(byCategory).map(([code, { label, amount }]) => {
    const found = budget?.categories.find((c) => c.code === code);
    return {
      label,
      budgetTotl: found?.budgetTotl ?? 0,
      remain: found?.remain ?? 0,
      amount,
    };
  });
};

// ===== AI 결과 반영 =====

type EntAiItem = {
  dtl_account?: unknown;
  name?: unknown;
  cost?: unknown;
  netAmount?: unknown;
  remarks?: unknown;
  remark?: unknown;
};

type EntAiResult = { status?: unknown; items?: unknown };

/** ai-draft-source 결과 — 진행 중엔 [null], 결과마다 status(success·error) 붙음 */
export const parseEntAiResult = (
  data: unknown,
): { result: EntAiResult[] } | { error?: string } | null => {
  const d = data as {
    status?: string | null;
    display?: { results?: unknown[] };
  } | null;
  if (!d || d.status === 'in_progress') return null;
  const results = (d.display?.results ?? []).filter(
    (r): r is EntAiResult => !!r && typeof r === 'object',
  );
  const hasSuccess = results.some(
    (r) => r.status === 'success' || r.status === undefined,
  );
  if (hasSuccess) return { result: results };
  if (results.some((r) => r.status === 'error')) return { error: '' };
  if (d.status && /fail|error/i.test(d.status)) return { error: '' };
  return null;
};

const formatAiCost = (value: unknown): string => {
  const n = toNumber(value);
  return Number.isFinite(n) ? formatEntNumber(n) : '';
};

type MatchTarget = { label: string; items: string[] };

/** 항목명 일치 → 항목명 부분 일치 → 분류명 부분 일치 → 분류명 '_' 조각 일치 순 */
const findBestMatch = (
  mapping: Record<string, MatchTarget>,
  name: string,
): { category: string; item: string } | null => {
  const q = name.trim().toLowerCase();
  if (!q) return null;
  const entries = Object.entries(mapping);
  for (const [code, data] of entries) {
    const hit = data.items.find((item) => item.toLowerCase() === q);
    if (hit) return { category: code, item: hit };
  }
  for (const [code, data] of entries) {
    const hit = data.items.find((item) => {
      const il = item.toLowerCase();
      return il.includes(q) || q.includes(il);
    });
    if (hit) return { category: code, item: hit };
  }
  for (const [code, data] of entries) {
    const label = data.label.toLowerCase();
    if (label.includes(q) || q.includes(label)) {
      return { category: code, item: '' };
    }
  }
  for (const [code, data] of entries) {
    const parts = data.label.toLowerCase().split('_');
    if (parts.some((part) => part.includes(q) || q.includes(part))) {
      return { category: code, item: '' };
    }
  }
  return null;
};

/** 지급 정보 표를 통째로 교체. 결과 항목이 없으면 null(표 유지) */
export const applyEntAiResult = (
  results: EntAiResult[],
  budget: EntBudget | null,
): { rows: EntExpenseRow[]; matched: boolean } | null => {
  const items = results.flatMap((r) =>
    Array.isArray(r.items) ? (r.items as EntAiItem[]) : [],
  );
  if (items.length === 0) return null;
  const mapping: Record<string, MatchTarget> = {};
  budget?.categories.forEach((c) => {
    mapping[c.code] = { label: c.label, items: getKstarItems(c.code) };
  });
  let matchedCount = 0;
  const rows = items.map((it) => {
    const name =
      (typeof it.dtl_account === 'string' && it.dtl_account) ||
      (typeof it.name === 'string' && it.name) ||
      '';
    const match = findBestMatch(mapping, name);
    if (match) matchedCount += 1;
    const remark =
      (typeof it.remarks === 'string' && it.remarks) ||
      (typeof it.remark === 'string' && it.remark) ||
      '';
    return createEntExpenseRow({
      category: match?.category ?? '',
      categoryLabel: match ? mapping[match.category].label : '',
      item: match?.item ?? '',
      cost: formatAiCost(
        it.cost !== null && it.cost !== undefined ? it.cost : it.netAmount,
      ),
      remark,
    });
  });
  return { rows, matched: matchedCount > 0 };
};

// ===== 검증 =====

export type EntExpenseViolation = {
  message: string;
  rowId?: string;
  field?: 'category' | 'item';
};

/** 임시저장·상신 공통 */
export const findEntExpenseViolation = (
  state: EntExpenseState | null | undefined,
): EntExpenseViolation | null => {
  if (!state?.budgetItem) return { message: '예산 항목을 선택해 주세요.' };
  for (const r of state.rows) {
    if (!r.category) {
      return {
        message: '분류를 선택해 주세요.',
        rowId: r.id,
        field: 'category',
      };
    }
    if (getKstarItems(r.category).length > 0 && !r.item) {
      return { message: '항목을 선택해 주세요.', rowId: r.id, field: 'item' };
    }
  }
  return null;
};

export const checkEntExpenseBeforeSave = (
  state: EntExpenseState | null | undefined,
): string | null => findEntExpenseViolation(state)?.message ?? null;

// ===== 저장 =====

type Labeled = { value: string; label: string };

export type EntExpenseDetail = {
  category: Labeled;
  item: Labeled;
  cost: number;
  remark: string;
};

export type EntExpenseAttributes = {
  subject: string;
  budgetItemValue: string;
  budgetItemLabel: string;
  budgetYear: string;
  budgetTotal: number;
  budgetRemaining: number;
  budgetUsageRate: number;
  details: EntExpenseDetail[];
  totalAmount: EntTotalAmountRow[];
};

export const buildEntExpenseAttributes = (
  state: EntExpenseState | null | undefined,
  subject: string,
): EntExpenseAttributes | null => {
  if (!state) return null;
  const details = state.rows.flatMap((r) => {
    const cost = numberOrZero(r.cost);
    const remark = r.remark.trim();
    if (!r.category && !r.item && cost === 0 && !remark) return [];
    return [
      {
        category: { value: r.category, label: r.categoryLabel },
        item: {
          value: r.item,
          label: r.item ? localize(r.item, { context: 'ent' }) : '',
        },
        cost,
        remark,
      },
    ];
  });
  const total = state.budget?.total ?? 0;
  const remaining = state.budget?.remaining ?? 0;
  return {
    subject,
    budgetItemValue: state.budgetItem,
    budgetItemLabel: state.budgetItemLabel,
    budgetYear: state.budgetYear,
    budgetTotal: Math.round(total),
    budgetRemaining: Math.round(remaining),
    budgetUsageRate: calcUsageRate(total, remaining),
    details,
    totalAmount: computeEntTotalAmount(state.rows, state.budget),
  };
};

// ===== 복원(편집·조회) =====

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const str = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const labeled = (value: unknown): Labeled => {
  const rec = asRecord(value);
  return { value: str(rec?.value), label: str(rec?.label) };
};

export const parseEntExpenseAttributes = (
  attributes: unknown,
): EntExpenseAttributes | null => {
  const a = asRecord(attributes);
  if (!a) return null;
  const details = Array.isArray(a.details) ? a.details : [];
  const totalAmount = Array.isArray(a.totalAmount) ? a.totalAmount : [];
  return {
    subject: str(a.subject),
    budgetItemValue: str(a.budgetItemValue),
    budgetItemLabel: str(a.budgetItemLabel),
    budgetYear: str(a.budgetYear),
    budgetTotal: numberOrZero(a.budgetTotal),
    budgetRemaining: numberOrZero(a.budgetRemaining),
    budgetUsageRate: numberOrZero(a.budgetUsageRate),
    details: details.map((d) => {
      const row = asRecord(d) ?? {};
      return {
        category: labeled(row.category),
        item: labeled(row.item),
        cost: numberOrZero(row.cost),
        remark: str(row.remark),
      };
    }),
    totalAmount: totalAmount.map((t) => {
      const row = asRecord(t) ?? {};
      return {
        label: str(row.label),
        budgetTotl: numberOrZero(row.budgetTotl),
        remain: numberOrZero(row.remain),
        amount: numberOrZero(row.amount),
      };
    }),
  };
};

/** 연도는 선택지에 있을 때만 복원, 분류는 예산 응답 도착 후 syncRowsToBudget 으로 거름 */
export const entExpenseAttributesToState = (
  saved: EntExpenseAttributes,
): EntExpenseState => {
  const years = getBudgetYearOptions();
  const rows = saved.details.map((d) =>
    createEntExpenseRow({
      category: d.category.value,
      categoryLabel: d.category.label,
      item: d.item.value,
      cost: d.cost ? formatEntNumber(d.cost) : '',
      remark: d.remark,
    }),
  );
  return {
    budgetItem: saved.budgetItemValue,
    budgetItemLabel: saved.budgetItemLabel,
    budgetYear: years.includes(saved.budgetYear)
      ? saved.budgetYear
      : getDefaultBudgetYear(),
    rows: rows.length ? rows : [createEntExpenseRow()],
    budget: null,
  };
};
